import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UserCircleIcon,
  BellIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
} from '../icons';
import { useAuth } from '../contexts/AuthContext';
const avatarImage = '/avatar.jpg';

const TABS = [
  { id: 'account', label: 'Account', icon: UserCircleIcon },
  { id: 'notifications', label: 'Notifications', icon: BellIcon },
];

const ToggleSwitch = ({ checked, onChange, id }) => (
  <button
    type="button"
    id={id}
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`md-switch ${checked ? 'md-switch-track-on' : 'md-switch-track-off'}`}
  >
    <span className={`md-switch-thumb ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
  </button>
);

const NestedCard = ({ title, subtitle, children, className = '' }) => (
  <div className={`md-card border border-matdash-border shadow-none h-full ${className}`.trim()}>
    <div className="md-card-body p-5">
      {title && <h4 className="text-base font-semibold text-matdash-dark mb-1">{title}</h4>}
      {subtitle && <p className="md-card-subtitle mb-4">{subtitle}</p>}
      {children}
    </div>
  </div>
);

const IconWell = ({ children }) => (
  <div className="md-icon-well md-icon-well-sm bg-matdash-body text-matdash-dark shrink-0">
    {children}
  </div>
);

const Profile = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = TABS.some((t) => t.id === tabParam) ? tabParam : 'account';

  const [avatarSrc, setAvatarSrc] = useState(avatarImage);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    location: 'Ghana',
    courtName: '',
    currency: 'Ghana (GHS)',
    address: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    notifyEmail: '',
  });

  const [prefs, setPrefs] = useState({
    newsletter: false,
    orderConfirmation: true,
    orderStatus: true,
    orderDelivered: false,
    emailNotification: true,
  });

  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
      email: user.email || '',
      phone: user.phone || '',
      courtName: user.court_name || user.courtName || '',
      notifyEmail: user.email || '',
      address: user.address || '',
    }));
  }, [user]);

  const setTab = (id) => {
    setSearchParams(id === 'account' ? {} : { tab: id }, { replace: true });
  };

  const updateField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleAvatarReset = () => setAvatarSrc(avatarImage);

  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setAvatarSrc(url);
  };

  return (
    <>
      <div className="md-card overflow-hidden">
        <ul className="flex flex-wrap border-b border-matdash-border list-none m-0 p-0" role="tablist">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <li key={tab.id} role="presentation">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setTab(tab.id)}
                  className={`md-account-tab ${isActive ? 'md-account-tab-active' : ''}`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="hidden md:inline">{tab.label}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="md-card-body">
          {activeTab === 'account' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <NestedCard
                  title="Change Profile"
                  subtitle="Change your profile picture from here"
                >
                  <div className="text-center">
                    <img
                      src={avatarSrc}
                      alt="Profile"
                      className="img-fluid rounded-full object-cover mx-auto"
                      width={120}
                      height={120}
                      style={{ width: 120, height: 120 }}
                    />
                    <div className="flex items-center justify-center gap-3 my-4">
                      <label className="md-btn md-btn-primary md-btn-sm cursor-pointer mb-0">
                        Upload
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif"
                          className="hidden"
                          onChange={handleAvatarUpload}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={handleAvatarReset}
                        className="md-btn md-btn-danger-subtle md-btn-sm"
                      >
                        Reset
                      </button>
                    </div>
                    <p className="text-sm text-matdash-muted mb-0">
                      Allowed JPG, GIF or PNG. Max size of 800K
                    </p>
                  </div>
                </NestedCard>

                <NestedCard
                  title="Change Password"
                  subtitle="To change your password please confirm here"
                >
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="current-password" className="block text-sm font-medium text-matdash-dark mb-1">
                        Current Password
                      </label>
                      <input
                        id="current-password"
                        type="password"
                        className="md-form-control"
                        value={form.currentPassword}
                        onChange={updateField('currentPassword')}
                        autoComplete="current-password"
                      />
                    </div>
                    <div>
                      <label htmlFor="new-password" className="block text-sm font-medium text-matdash-dark mb-1">
                        New Password
                      </label>
                      <input
                        id="new-password"
                        type="password"
                        className="md-form-control"
                        value={form.newPassword}
                        onChange={updateField('newPassword')}
                        autoComplete="new-password"
                      />
                    </div>
                    <div>
                      <label htmlFor="confirm-password" className="block text-sm font-medium text-matdash-dark mb-1">
                        Confirm Password
                      </label>
                      <input
                        id="confirm-password"
                        type="password"
                        className="md-form-control"
                        value={form.confirmPassword}
                        onChange={updateField('confirmPassword')}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                </NestedCard>
              </div>

              <NestedCard
                title="Personal Details"
                subtitle="To change your personal detail, edit and save from here"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="full-name" className="block text-sm font-medium text-matdash-dark mb-1">
                        Your Name
                      </label>
                      <input
                        id="full-name"
                        type="text"
                        className="md-form-control"
                        placeholder="Your full name"
                        value={form.name}
                        onChange={updateField('name')}
                      />
                    </div>
                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-matdash-dark mb-1">
                        Location
                      </label>
                      <select
                        id="location"
                        className="md-form-select"
                        value={form.location}
                        onChange={updateField('location')}
                      >
                        <option>Ghana</option>
                        <option>United Kingdom</option>
                        <option>United States</option>
                        <option>India</option>
                        <option>Nigeria</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-matdash-dark mb-1">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        className="md-form-control"
                        placeholder="you@court.gov"
                        value={form.email}
                        onChange={updateField('email')}
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="court-name" className="block text-sm font-medium text-matdash-dark mb-1">
                        Court Name
                      </label>
                      <input
                        id="court-name"
                        type="text"
                        className="md-form-control"
                        placeholder="Assigned court"
                        value={form.courtName}
                        onChange={updateField('courtName')}
                      />
                    </div>
                    <div>
                      <label htmlFor="currency" className="block text-sm font-medium text-matdash-dark mb-1">
                        Currency
                      </label>
                      <select
                        id="currency"
                        className="md-form-select"
                        value={form.currency}
                        onChange={updateField('currency')}
                      >
                        <option>Ghana (GHS)</option>
                        <option>US Dollar ($)</option>
                        <option>United Kingdom (Pound)</option>
                        <option>Euro (€)</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-matdash-dark mb-1">
                        Phone
                      </label>
                      <input
                        id="phone"
                        type="text"
                        className="md-form-control"
                        placeholder="+233 …"
                        value={form.phone}
                        onChange={updateField('phone')}
                      />
                    </div>
                  </div>
                  <div className="lg:col-span-2">
                    <label htmlFor="address" className="block text-sm font-medium text-matdash-dark mb-1">
                      Address
                    </label>
                    <input
                      id="address"
                      type="text"
                      className="md-form-control"
                      placeholder="Street, city, country"
                      value={form.address}
                      onChange={updateField('address')}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3 mt-5">
                  <button type="button" className="md-btn md-btn-primary md-btn-sm">Save</button>
                  <button type="button" className="md-btn md-btn-danger-subtle md-btn-sm">Cancel</button>
                </div>
              </NestedCard>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <NestedCard
                title="Notification Preferences"
                subtitle="Select the notifications you would like to receive via email. Service messages such as security or legal notices cannot be disabled."
              >
                <div className="mb-6 max-w-xl">
                  <label htmlFor="notify-email" className="block text-sm font-medium text-matdash-dark mb-1">
                    Email Address*
                  </label>
                  <input
                    id="notify-email"
                    type="email"
                    className="md-form-control"
                    value={form.notifyEmail}
                    onChange={updateField('notifyEmail')}
                    required
                  />
                  <p className="text-xs text-matdash-muted mt-1 mb-0">Required for notifications.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    { key: 'newsletter', title: 'Our newsletter', desc: "We'll always let you know about important changes", icon: DocumentTextIcon },
                    { key: 'orderConfirmation', title: 'Session ready', desc: 'Notify when a transcription job completes', icon: CheckCircleIcon },
                    { key: 'orderStatus', title: 'Status changes', desc: 'Notify when a session status changes', icon: ClockIcon },
                    { key: 'orderDelivered', title: 'Certification reminders', desc: 'Reminders for drafts awaiting certification', icon: CheckCircleIcon },
                    { key: 'emailNotification', title: 'Email Notification', desc: 'Turn on email notification to get updates', icon: EnvelopeIcon },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.key} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <IconWell><Icon className="h-5 w-5" /></IconWell>
                          <div className="min-w-0">
                            <h5 className="text-sm font-semibold text-matdash-dark mb-0">{item.title}</h5>
                            <p className="text-xs text-matdash-muted mb-0">{item.desc}</p>
                          </div>
                        </div>
                        <ToggleSwitch
                          id={`pref-${item.key}`}
                          checked={prefs[item.key]}
                          onChange={(v) => setPrefs((p) => ({ ...p, [item.key]: v }))}
                        />
                      </div>
                    );
                  })}
                </div>
              </NestedCard>

              <div className="flex items-center justify-end gap-3">
                <button type="button" className="md-btn md-btn-primary md-btn-sm">Save</button>
                <button type="button" className="md-btn md-btn-danger-subtle md-btn-sm">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Profile;
